import React from 'react';
import { ActionButtons } from '@/pages/WorkflowRuns/components/ActionButtons';
import { ContentTabs } from '@/pages/WorkflowRuns/components/ContentTabs';
import { FlowVisualization } from '@/pages/WorkflowRuns/components/FlowVisualization';
import { catalog_show_path, workflow_run_path } from '@/lib/routes';
import { router } from '@inertiajs/react';
import { SidebarItem } from '@/pages/WorkflowRuns/components/SidebarItem';
import { useWorkflowRunPolling } from '@/hooks/useWorkflowRunPolling';
import { generateTabs } from './utils/tabUtils';
import type { WorkflowRun, SidebarItemData } from './types';
import { ChevronLeft, ChevronDown, Play, RotateCcw, Copy, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const TERMINAL_STATUSES = ['completed', 'failed'];

const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const IdBadge = ({
  label,
  value,
  truncate,
}: {
  label: string;
  value: string;
  truncate?: boolean;
}) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors font-mono"
      title={`${value} (click to copy)`}
    >
      <span className="text-zinc-400 dark:text-zinc-500">{label}:</span>
      <span>{truncate ? `${value.slice(0, 8)}...` : value}</span>
      {copied ? (
        <Check size={12} className="text-emerald-500" />
      ) : (
        <Copy size={12} className="text-zinc-400" />
      )}
    </button>
  );
};

const TimeBadge = ({ label, value }: { label: string; value: string }) => {
  return (
    <span
      className="flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 font-mono"
      title={new Date(value).toLocaleString()}
    >
      <span className="text-zinc-400 dark:text-zinc-500">{label}:</span>
      <span>{formatRelativeTime(value)}</span>
    </span>
  );
};

interface WorkflowRunProps {
  workflowRun: WorkflowRun;
}

const Index = ({ workflowRun: initialWorkflowRun }: WorkflowRunProps) => {
  const workflowRun = useWorkflowRunPolling(initialWorkflowRun);

  const workflowItem = React.useMemo(
    (): SidebarItemData => ({
      id: 'workflow',
      title: 'Workflow',
      description: workflowRun.workflowName,
      status: workflowRun.status as 'scheduled' | 'in_progress' | 'completed' | 'failed',
      input: workflowRun.workflowInput,
      output: workflowRun.workflowOutput,
    }),
    [workflowRun]
  );

  const [activeItem, setActiveItem] = React.useState<SidebarItemData>(workflowItem);
  const [activeTab, setActiveTab] = React.useState(() =>
    TERMINAL_STATUSES.includes(initialWorkflowRun.status) ? 'output' : 'input'
  );

  React.useEffect(() => {
    const updatedTask = workflowRun.tasks.find((task) => task.id === activeItem.id);
    if (updatedTask) {
      setActiveItem(updatedTask);
    }
  }, [workflowRun]);

  const handleTaskClick = (item: SidebarItemData) => {
    setActiveItem(item);
    if (activeTab !== 'graph') {
      setActiveTab(TERMINAL_STATUSES.includes(item.status) ? 'output' : 'input');
    }
  };

  const flowContent = (
    <FlowVisualization
      workflowRun={workflowRun}
      activeItem={activeItem}
      onItemClick={handleTaskClick}
    />
  );

  const statusColor =
    {
      completed: 'bg-emerald-500',
      in_progress: 'bg-blue-500',
      scheduled: 'bg-amber-500',
      failed: 'bg-red-500',
    }[workflowRun.status] || 'bg-zinc-400';

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-zinc-50 dark:bg-zinc-950">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 flex-shrink-0">
        <div className="flex flex-col gap-3">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
            <a
              href="/workflows"
              className="hover:text-zinc-900 dark:hover:text-zinc-100 flex items-center gap-1 transition-colors"
            >
              <ChevronLeft size={16} />
              <span>All Workflows</span>
            </a>
            <span className="text-zinc-300 dark:text-zinc-600">•</span>
            <span className="font-mono text-xs">
              {workflowRun.id ? `${workflowRun.id.slice(0, 8)}...` : 'New Run'}
            </span>
            <div className={cn('w-2 h-2 rounded-full ml-1', statusColor)} />
          </nav>

          {/* Title row */}
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                {workflowRun.workflowName}
              </h1>
              <div className="flex items-center gap-3 text-xs text-zinc-500 dark:text-zinc-400 flex-wrap">
                {workflowRun.workflowPath?.trim() && (
                  <a
                    href={catalog_show_path(workflowRun.provider, workflowRun.functionName)}
                    className="font-mono hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
                  >
                    {workflowRun.workflowPath.replace(/^packages\/temporal\/src\/workflows\//, '')}
                  </a>
                )}
                <IdBadge label="Workflow ID" value={workflowRun.workflowId} />
                <IdBadge label="Run ID" value={workflowRun.id} truncate />
                <TimeBadge label="Started" value={workflowRun.startTime} />
                {workflowRun.endTime && <TimeBadge label="Ended" value={workflowRun.endTime} />}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 rounded-lg transition-colors">
                    <RotateCcw size={14} />
                    <span>Rerun</span>
                    <ChevronDown size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        const csrfToken =
                          document
                            .querySelector('meta[name="csrf-token"]')
                            ?.getAttribute('content') || '';
                        const provider = workflowRun.provider;
                        const response = await fetch(
                          `/workflows/${workflowRun.functionName}/runs?provider=${provider}`,
                          {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              Accept: 'application/json',
                              'X-CSRF-Token': csrfToken,
                            },
                            body: JSON.stringify({
                              workflow_name: workflowRun.functionName,
                              args: workflowRun.workflowInput,
                            }),
                          }
                        );
                        if (!response.ok) {
                          throw new Error('Failed to start workflow');
                        }
                        const data = await response.json();
                        if (data.workflowId && data.runId) {
                          toast.success('Workflow started');
                          window.location.href = workflow_run_path(data.workflowId, data.runId, {
                            provider: data.provider,
                          });
                        } else {
                          throw new Error('Invalid response');
                        }
                      } catch (e) {
                        toast.error(e instanceof Error ? e.message : 'Failed to start workflow');
                      }
                    }}
                  >
                    <Play size={14} className="mr-2" />
                    Run with same input
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      router.visit(
                        `${catalog_show_path(workflowRun.provider, workflowRun.functionName)}?tab=run&rerunFrom=${workflowRun.workflowId}:${workflowRun.id}`
                      );
                    }}
                  >
                    <RotateCcw size={14} className="mr-2" />
                    Edit input and run
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ActionButtons
                workflow={{ path: workflowRun.workflowPath }}
                workflowId={workflowRun.workflowId}
                runId={workflowRun.id}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 min-h-0 flex">
        {/* Sidebar */}
        <aside className="w-80 flex-shrink-0 h-full overflow-y-auto bg-white dark:bg-zinc-900 border-r border-zinc-200 dark:border-zinc-800">
          <div className="py-2">
            <SidebarItem
              key={workflowItem.id}
              {...workflowItem}
              type="workflow"
              isActive={activeItem.id === workflowItem.id}
              onClick={() => handleTaskClick(workflowItem)}
            />

            {workflowRun.tasks.length > 0 && (
              <div className="px-4 py-2">
                <div className="text-xs font-medium text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                  Tasks ({workflowRun.tasks.length})
                </div>
              </div>
            )}

            {workflowRun.tasks.map((task) => (
              <SidebarItem
                key={task.id}
                {...task}
                type="task"
                isActive={activeItem.id === task.id}
                onClick={() => handleTaskClick(task)}
              />
            ))}
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 h-full overflow-hidden bg-white dark:bg-zinc-900">
          <div className="h-full w-full min-w-0 flex flex-col overflow-hidden">
            <ContentTabs
              tabs={generateTabs(activeItem, workflowRun, flowContent)}
              value={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Index;
